import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FiAlertCircle, FiCheck, FiX } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { AnnouncementRow } from '../../../model/CommunicationsAnnouncementsResult';
import { Announcement } from '../../../model/Stock';
import api, { combinedTicker } from '../../../api/api';
import { toast } from '../Toast';

interface Props {
    row: AnnouncementRow;
    onClose: () => void;
    onDeleted: () => void;
}

// spec 2:53997 "Modal / Delete announcement". Omitted (user decision): draft-email line, "Type DELETE to confirm".
// Same soft delete as v1: the handler matches on headline + date.
const DeleteAnnouncementModal: React.FC<Props> = ({ row, onClose, onDeleted }) => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const remove = async () => {
        setDeleting(true);
        const announcement = { headline: row.headline, date: row.date } as unknown as Announcement;
        const result = await api().deleteAnnouncement(combinedTicker(stock.ticker, stock.exchange), announcement).catch(e => ({ success: false, errorMessage: e.message }));
        setDeleting(false);
        if (!result.success) { toast.error(`Delete failed: ${result.errorMessage}`); return; }
        toast.success('Announcement deleted');
        onDeleted();
    };

    return <div className="delete-announcement-overlay" onClick={onClose}>
        <div className="delete-announcement-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
                <span className="icon"><FiAlertCircle /></span>
                <div className="t">
                    <strong>Delete This Announcement?</strong>
                    <span>“{row.headline}” will be removed from Investi and the Website Hub.</span>
                </div>
                <button type="button" className="close" aria-label="Close" onClick={onClose}><FiX /></button>
            </div>
            <div className="modal-body">
                <div className="line"><FiCheck /> The announcement stays on the ASX — only the Investi copy is removed</div>
                {row.views > 0 && <div className="line warn"><FiAlertCircle /> It’s already had {row.views.toLocaleString()} {row.views === 1 ? 'view' : 'views'}</div>}
            </div>
            <div className="modal-footer">
                <button type="button" className="btn-keep" onClick={onClose}>Keep It</button>
                <button type="button" className="btn-delete" onClick={remove} disabled={deleting}>Delete Announcement</button>
            </div>
        </div>
    </div>;
};

export default DeleteAnnouncementModal;
